// IMPORTANT: After modifying this file, update CHANGELOG.md with a summary of your changes.
// Add clear comments for non-obvious changes, including what replaced prior behavior, to avoid repeating failed fixes.

"use client";

import { useEffect, useState } from "react";

/**
 * Cookie / analytics consent banner.
 *
 * On first visit: shows a fixed bottom-right card with Allow / Decline.
 *
 * On Allow:
 *   - Records `granted` in localStorage (`opichi-consent`).
 *   - Walks every `<script type="text/plain" data-requires-consent="1">`
 *     placeholder rendered by the root layout, copies the inert script
 *     content into a fresh executing `<script>` tag, and appends it
 *     where the placeholder lived. This is the standard "consent mode"
 *     pattern — the browser only ever runs the analytics code AFTER
 *     consent, so no cookies are set until then.
 *
 * On Decline:
 *   - Records `denied` in localStorage. Placeholders stay inert.
 *
 * On subsequent visits:
 *   - If `granted`: re-activates placeholders silently on every page
 *     load (the placeholders re-render on each navigation).
 *   - If `denied`: hides the banner forever and leaves placeholders
 *     untouched. A future "Manage cookie preferences" link could
 *     clear the localStorage value to re-prompt; out of scope here.
 *
 * Why client-side activation instead of a server-side cookie + SSR
 * branch: keeps the public route cache key uniform (every visitor
 * gets the same HTML, then the banner branches client-side based on
 * localStorage). Avoids cookie-vary cache invalidation entirely.
 */

const STORAGE_KEY = "opichi-consent";
type ConsentValue = "granted" | "denied";

function activateGatedScripts() {
  if (typeof document === "undefined") return;
  const placeholders = document.querySelectorAll<HTMLScriptElement>(
    'script[type="text/plain"][data-requires-consent="1"]'
  );
  placeholders.forEach((node) => {
    const next = document.createElement("script");
    // Copy any other attributes the admin may have set on the original
    // (src, async, defer, etc.) — keeps inline AND external scripts
    // working through the same path.
    for (const attr of Array.from(node.attributes)) {
      if (attr.name === "type" || attr.name === "data-requires-consent") {
        continue;
      }
      next.setAttribute(attr.name, attr.value);
    }
    next.text = node.text;
    node.parentNode?.insertBefore(next, node);
    node.remove();
  });
}

export function ConsentBanner() {
  // `null` while we figure out the persisted state — prevents the banner
  // from flashing on screen for a frame on visits where the user has
  // already chosen.
  const [decision, setDecision] = useState<ConsentValue | null | undefined>(
    undefined
  );

  useEffect(() => {
    let stored: string | null = null;
    try {
      stored = window.localStorage.getItem(STORAGE_KEY);
    } catch {
      // localStorage blocked (private mode, etc.) — banner shows; choice
      // doesn't persist, which is fine.
    }

    if (stored === "granted") {
      setDecision("granted");
      activateGatedScripts();
    } else if (stored === "denied") {
      setDecision("denied");
    } else {
      setDecision(null);
    }
  }, []);

  function persist(value: ConsentValue) {
    try {
      window.localStorage.setItem(STORAGE_KEY, value);
    } catch {
      // ignore — UX still works without persistence
    }
    setDecision(value);
    if (value === "granted") activateGatedScripts();
  }

  // While loading, or after a decision, render nothing.
  if (decision !== null) return null;

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:max-w-md z-[60] brutal-border bg-white p-5 shadow-lg"
    >
      <h2 className="font-[family-name:var(--font-display)] text-xl mb-2">
        We use cookies
      </h2>
      <p className="text-sm text-stone-700 leading-relaxed mb-4">
        We use analytics cookies to understand how visitors use the site.
        They&rsquo;re off until you opt in.
      </p>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => persist("granted")}
          className="brutal-border-sm bg-black text-white px-4 py-2 uppercase tracking-widest text-xs hover:bg-stone-800 transition-colors cursor-pointer"
        >
          Allow analytics
        </button>
        <button
          type="button"
          onClick={() => persist("denied")}
          className="brutal-border-sm bg-white text-stone-700 px-4 py-2 uppercase tracking-widest text-xs hover:bg-stone-100 transition-colors cursor-pointer"
        >
          Decline
        </button>
      </div>
    </div>
  );
}
